// "Routines" are marketed as "Agents" in the UI; the code name remains "routines".
import {getTestProps} from '@benchling/js-test-utils';
import {Box, Card, createStyles, Flex, Group, Loader, Stack, Text, Title} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {CHAT_CONTENT_MAX_WIDTH_PX} from 'llm/chat-library/ui/AIStyleConstants';
import {CenteredSpinner} from 'ui-library/Spinner';
import fuzzydate from 'util/fuzzydate';

import RoutineDmChatInput from './RoutineDmChatInput';
import {RoutineRunsList} from './RoutineRunsTab';
import useAgentHomepageRoutineExecutions from './useAgentHomepageRoutineExecutions';
import useRoutine from './useRoutine';

interface AgentHomepageProps {
  routineId: string;
  /** Wide sidepanel. Minimized uses the shorter top offset. */
  isExpanded?: boolean;
}

const OVERVIEW_TOP_OFFSET_EXPANDED = '15vh';
// Minimized panel and a browser at most 900px wide share this rest position.
const OVERVIEW_TOP_OFFSET_COMPACT = '2.85vh';
const OVERVIEW_NARROW_MAX_WIDTH_PX = 900;
/** @visibleForTesting */
export const OVERVIEW_FADE_OUT_MS = 180;
const OVERVIEW_FADE_IN_MS = 260;
const OVERVIEW_FADE_IN_DELAY_MS = 120;
const OVERVIEW_FADE_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

const useStyles = createStyles(
  (theme, {offsetExpanded, fadingOut}: {offsetExpanded: boolean; fadingOut: boolean}) => ({
    // Fill the (bounded, scrolling) sidepanel content slot so the composer can pin to its bottom
    // rather than sitting at the end of the page's scroll.
    root: {
      flex: 1,
      minHeight: 0,
    },
    // The Overview + Agent activity scroll here; the composer footer stays put below it.
    scrollRegion: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    // Pinned composer bar. No divider: the composer sits flush against the activity list above it.
    footer: {
      flexShrink: 0,
      backgroundColor: theme.white,
    },
    overviewContainer: {
      maxWidth: CHAT_CONTENT_MAX_WIDTH_PX,
    },
    totalRunsCount: {
      fontSize: theme.headings.sizes.h2.fontSize,
    },
    // Matches the loaded card: padding, the "Total runs" line, the gap above the count, and the count line.
    totalRunsCard: {
      minHeight: `calc(${theme.spacing.lg} * 2 + ${theme.fontSizes.sm} * ${theme.lineHeight} + ${theme.spacing.md} + ${theme.headings.sizes.h2.fontSize} * ${theme.lineHeight})`,
    },
    // Expanded starts further down than minimized. The offset is inside the scroll region, so a long
    // activity list scrolls it away and scrolling back to the top restores it. Opacity fades the block
    // out in place, then back in after the offset has moved.
    overviewTopOffset: {
      paddingTop: offsetExpanded ? OVERVIEW_TOP_OFFSET_EXPANDED : OVERVIEW_TOP_OFFSET_COMPACT,
      opacity: fadingOut ? 0 : 1,
      pointerEvents: fadingOut ? 'none' : undefined,
      // Quick fade out, then a short pause before a quick fade in at the new offset.
      transition: fadingOut
        ? `opacity ${OVERVIEW_FADE_OUT_MS}ms ${OVERVIEW_FADE_EASING}`
        : `opacity ${OVERVIEW_FADE_IN_MS}ms ${OVERVIEW_FADE_EASING} ${OVERVIEW_FADE_IN_DELAY_MS}ms`,
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
      // At this width or narrower, expanded and minimized share one rest position. Height still
      // scales through vh.
      [`@media (max-width: ${OVERVIEW_NARROW_MAX_WIDTH_PX}px)`]: {
        paddingTop: OVERVIEW_TOP_OFFSET_COMPACT,
      },
    },
  })
);

/**
 * Keeps the current offset while the overview fades out, then shows it at the new offset.
 * Reduced motion moves immediately, with no fade. A timer ends the fade-out, because transitionend
 * does not fire if the fading element is replaced or another toggle cancels the fade-in.
 */
function useOverviewOffsetFade(isExpanded: boolean): {offsetExpanded: boolean; fadingOut: boolean} {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [offsetExpanded, setOffsetExpanded] = useState(isExpanded);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setOffsetExpanded(isExpanded);
      setFadingOut(false);
      return;
    }
    if (isExpanded === offsetExpanded) {
      setFadingOut(false);
      return;
    }
    setFadingOut(true);
    const timeout = window.setTimeout(() => {
      setOffsetExpanded(isExpanded);
      setFadingOut(false);
    }, OVERVIEW_FADE_OUT_MS);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [isExpanded, offsetExpanded, prefersReducedMotion]);

  return {offsetExpanded, fadingOut};
}

export default function AgentHomepage({
  routineId,
  isExpanded = true,
}: AgentHomepageProps): JSX.Element | null {
  const {offsetExpanded, fadingOut} = useOverviewOffsetFade(isExpanded);
  const isNarrowViewport = useMediaQuery(`(max-width: ${OVERVIEW_NARROW_MAX_WIDTH_PX}px)`);
  const {classes, cx} = useStyles({offsetExpanded, fadingOut});
  const {llmEnableRoutineHomepage} = useRolloutFlags();
  const homepageEnabled = llmEnableRoutineHomepage === true;
  const directMessagingEnabled = BenchlingConfig.LLM_ENABLE_DIRECT_AGENT_RUNS;

  const {routine, isLoading, isError} = useRoutine({routineId, enabled: homepageEnabled});

  const {
    total,
    runs,
    isLoading: executionsLoading,
    isError: executionsError,
    hasMore,
    loadMore,
  } = useAgentHomepageRoutineExecutions({
    routineId,
    enabled: homepageEnabled && routine != null,
  });

  if (!homepageEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Box className={cx(classes.overviewContainer, classes.overviewTopOffset)} mx="auto" w="100%">
        <CenteredSpinner />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        className={cx(classes.overviewContainer, classes.overviewTopOffset)}
        mx="auto"
        px="md"
        pb="lg"
        w="100%"
        {...getTestProps({component: 'AgentHomepage', element: 'root'})}
      >
        <Text size="sm" c="dimmed">
          Failed to load this Agent. Please try again.
        </Text>
      </Box>
    );
  }

  if (routine == null) {
    return (
      <Box
        className={cx(classes.overviewContainer, classes.overviewTopOffset)}
        mx="auto"
        px="md"
        pb="lg"
        w="100%"
        {...getTestProps({component: 'AgentHomepage', element: 'root'})}
      >
        <Text size="sm" c="dimmed">
          This Agent could not be found.
        </Text>
      </Box>
    );
  }

  const name = routine.name ?? 'Agent';
  const canStartChat = directMessagingEnabled && routine.permissions.appendable;
  const {triggerType} = routine;
  // Executions come back newest-first, so the first item is the most recent run. Only surface the
  // stats once the executions query has resolved successfully; while it is pending or errored, the
  // total and newest run are unknown rather than genuinely 0 / absent.
  const newestRun = !executionsLoading && !executionsError && runs.length > 0 ? runs[0] : null;

  const totalRuns = executionsLoading ? (
    <Loader size="sm" aria-label="Loading total runs" />
  ) : executionsError ? (
    <Text size="sm" c="dimmed">
      Unable to load
    </Text>
  ) : (
    <Text fw={700} pt="md" className={classes.totalRunsCount}>
      {total}
    </Text>
  );

  return (
    <Flex
      direction="column"
      className={classes.root}
      {...getTestProps({component: 'AgentHomepage', element: 'root'})}
    >
      <Box className={classes.scrollRegion}>
        <Box
          className={cx(classes.overviewContainer, classes.overviewTopOffset)}
          mx="auto"
          px="md"
          pb="lg"
          w="100%"
          {...getTestProps({component: 'AgentHomepage', element: 'overviewContent'})}
        >
          <Stack spacing="lg">
            <Group spacing="sm" align="baseline">
              <Title order={2}>Overview</Title>
              {newestRun != null && (
                <Text size="sm" c="dimmed">
                  Last run {fuzzydate(newestRun.createdAt)}
                </Text>
              )}
            </Group>

            <Card className={classes.totalRunsCard} radius="lg" padding="lg" bg="blue.0">
              <Text size="sm" c="dimmed">
                Total runs
              </Text>
              {totalRuns}
            </Card>

            <Stack spacing="sm">
              <Title order={3} fz="lg">
                Agent activity
              </Title>
              <RoutineRunsList
                runs={runs}
                isLoading={executionsLoading}
                isError={executionsError}
                hasMore={hasMore}
                loadMore={loadMore}
                routineName={name}
                showExecutionKind={true}
                triggerType={triggerType}
                inlineMetadata={isExpanded && !isNarrowViewport}
                metadataColor="gray.6"
                illustratedEmptyState={true}
                routineId={routineId}
                canStartChat={canStartChat}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {canStartChat && (
        <Box className={classes.footer}>
          <Box className={classes.overviewContainer} mx="auto" px="md" py="md" w="100%">
            <RoutineDmChatInput key={routine.id} routine={routine} />
          </Box>
        </Box>
      )}
    </Flex>
  );
}
